import { FC, SetStateAction, useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { createGlobalStyle } from 'styled-components';
import {
  Customer,
  CustomForm,
  CustomFormPageFieldUuidToFileEntitiesMap,
  CustomFormReport,
  CustomFormReportFormData,
  CustomFormReportType,
  CustomFormReportView,
  ErrorView,
  FileEntity,
  FileEntityUuidToFileMap,
  FpCompany,
  MainLayout,
  PdfPreviewGenerationStatus,
  SuccessPage,
  ToastService,
  useIsMobileOrTablet,
  usePreloaderContext,
  User,
  wait,
  LocalizationContextProvider
} from '@flicent/fieldpulse-react-component-library-frontend';

import {
  fillPublicLinkCustomFormReportFormRequest,
  getCustomFormReportPublicLinkRequest,
  viewPublicLinkCustomFormReportFormRequest,
  createPublicLinkCustomFormReportFormFileFieldsRequest,
  modifyPublicLinkCustomFormReportFormFileFieldsRequest,
  deletePublicLinkCustomFormReportFormFileFieldsRequest,
} from '@/services';
import { OPEN_NOT_FROM_EMAIL_SEARCH_PARAM, OPEN_NOT_FROM_EMAIL_SEARCH_PARAM_YES_VALUE } from '@/constants';
import { uploadCustomFormFieldsFilesUpdates, UploadCustomFormFieldsFilesUpdatesReturnType } from "@/utils";
import { useListenForPublicLinkCustomFormWebsocketMessages } from '@/hooks';
import { useCustomFormAnalyticsPublicLinkContext } from '@/contexts';
import { useCustomFormTranslation } from '../i18n';


const GlobalStyle = createGlobalStyle`
  html, body, #root {
    height: 100%;
  }
`;

type UriParams = {
  publicLinkId: string;
}

interface PublicLinkPageProps {
  onChangePageTitle: (title: string) => void;
}

export const PublicLinkPage: FC<PublicLinkPageProps> = ({ onChangePageTitle }) => {
  const { publicLinkId } = useParams<UriParams>();

  const { t } = useCustomFormTranslation();
  const [searchParams] = useSearchParams();
  const openNotFromEmail: boolean = searchParams.get(OPEN_NOT_FROM_EMAIL_SEARCH_PARAM) === OPEN_NOT_FROM_EMAIL_SEARCH_PARAM_YES_VALUE;

  const isMobileOrTablet: boolean = useIsMobileOrTablet();
  const preloaderContext = usePreloaderContext();
  const { trackFormSubmitted, trackFormSigned } = useCustomFormAnalyticsPublicLinkContext();

  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [reportType, setReportType] = useState<CustomFormReportType>('fill');
  const [publicLinkAuthor, setPublicLinkAuthor] = useState<User | null>(null);
  const [company, setCompany] = useState<FpCompany | null>(null);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [publicLinkFormsData, setPublicLinkFormsData] = useState<CustomFormReportFormData[]>([]);
  const [initialPublicLinkFormsData, setInitialPublicLinkFormsData] = useState<CustomFormReportFormData[]>([]);
  const [failedToCreateFileEntitiesUuids, setFailedToCreateFileEntitiesUuids] = useState<string[]>([]);
  const [formReport, setFormReport] = useState<CustomFormReport | null>(null);
  const [fileEntityUuidToFileMap, setFileEntityUuidToFileMap] = useState<FileEntityUuidToFileMap>({});
  const [isPublicLinkFilled, setIsPublicLinkFilled] = useState<boolean>(false);
  const [pdfPreviewGenerationStatus, setPdfPreviewGenerationStatus] = useState<PdfPreviewGenerationStatus | null>(null);

  useListenForPublicLinkCustomFormWebsocketMessages({
    encryptedPublicLinkId: publicLinkId || null,
    onFillPdfProgress: (progress: number, currentStep: string) => {
      setPdfPreviewGenerationStatus((prevResult) => ({
        pdfFileUrl: null,
        ...prevResult,
        progress,
        currentStep,
        errorDetail: null,
      }));
    },
    onFillPdfGenerated: (pdfFileUrl: string) => {
      setPdfPreviewGenerationStatus({
        progress: 100,
        currentStep: t('publicLinkPage.pdfGenerated'),
        pdfFileUrl: pdfFileUrl,
        errorDetail: null,
      });
    },
    onFillPdfGenerationError: (errorDetail: string) => {
      setPdfPreviewGenerationStatus((prevResult) => ({
        ...prevResult,
        pdfFileUrl: null,
        progress: 0,
        currentStep: '',
        errorDetail,
      }));
    },
  });

  const setPublicLinkFormsDataAndInitial = (data: SetStateAction<CustomFormReportFormData[]>) => {
    setPublicLinkFormsData(data);
    setInitialPublicLinkFormsData(data);
  };

  const updateFormInPublicLinkFormsData = (formId: number, formData: Partial<CustomFormReportFormData>, setter: (data: SetStateAction<CustomFormReportFormData[]>) => void) => {
    setter((prevData) => {
      return prevData.map((data) => {
        if (data.form.id === formId) {
          return {
            ...data,
            ...formData
          };
        }

        return data;
      });
    })
  }

  const updateInitialFormData = (formId: number, formData: Partial<CustomFormReportFormData>) => {
    updateFormInPublicLinkFormsData(formId, formData, setInitialPublicLinkFormsData);
  };

  const updateCurrentFormData = (formId: number, formData: Partial<CustomFormReportFormData>) => {
    updateFormInPublicLinkFormsData(formId, formData, setPublicLinkFormsData);
  };

  // Fetch data
  useEffect(() => {
    if (!publicLinkId) {
      setError(t('publicLinkPage.invalidPublicLinkId'));
      return;
    }

    preloaderContext.showFullPagePreloader();

    getCustomFormReportPublicLinkRequest({ publicLinkId })
      .then(({ report_type, public_link_author, company, customer, custom_form_report, forms_data, is_public_link_filled_status, filled_pdf_generation_progress, filled_pdf_generation_error_detail, filled_pdf_file_url }) => {
        setReportType(report_type);
        setPublicLinkAuthor(public_link_author);
        setCompany(company);
        setCustomer(customer);
        setPublicLinkFormsDataAndInitial(forms_data);
        setFormReport(custom_form_report);
        setIsPublicLinkFilled(is_public_link_filled_status);

        if (filled_pdf_generation_progress || filled_pdf_file_url || filled_pdf_generation_error_detail) {
          setPdfPreviewGenerationStatus({
            progress: filled_pdf_generation_progress?.progress || (filled_pdf_file_url ? 100 : 0),
            currentStep: filled_pdf_generation_progress?.current_step || (filled_pdf_file_url ? t('publicLinkPage.pdfGenerated') : ''),
            pdfFileUrl: filled_pdf_file_url,
            errorDetail: filled_pdf_generation_error_detail,
          });
        }

        if (report_type === 'view' && !openNotFromEmail && forms_data.some(({ form }) => !form.viewed_at)) {
          viewPublicLinkCustomFormReportFormRequest({ publicLinkId })
            .then(({ viewed_at }) => {
              for (const { form } of forms_data) {
                updateCurrentFormData(form.id, { form: { ...form, viewed_at } });
              }
            });
        }
      })
      .catch((error) => {
        setError(error.message);
      })
      .finally(() => {
        setLoading(false);
        preloaderContext.hideFullPagePreloader();
      });
  }, [publicLinkId, openNotFromEmail]);

  const uploadPublicLinkFieldsFiles = async (
    form: CustomForm,
    pageFieldUuidToFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap,
    fileEntityUuidToFileMap: FileEntityUuidToFileMap,
  ): Promise<UploadCustomFormFieldsFilesUpdatesReturnType> => {
    const initialPageFieldUuidToFileEntitiesMap = initialPublicLinkFormsData.find((data) => data.form.id === form.id)?.page_field_uuid_to_file_entities_map || {};

    const result = await uploadCustomFormFieldsFilesUpdates(
      t,
      false,
      isMobileOrTablet,
      initialPageFieldUuidToFileEntitiesMap,
      pageFieldUuidToFileEntitiesMap,
      fileEntityUuidToFileMap,
      () => Promise.resolve({}),
      (params) => createPublicLinkCustomFormReportFormFileFieldsRequest({
        publicLinkId: publicLinkId!,
        formId: form.id,
        ...params,
      }).then(({ created_file_dto }) => created_file_dto),
      (fileEntities: FileEntity[]) => modifyPublicLinkCustomFormReportFormFileFieldsRequest({
        publicLinkId: publicLinkId!,
        formId: form.id,
        fileEntities,
      }),
      (fileEntitiesIds: number[]) => deletePublicLinkCustomFormReportFormFileFieldsRequest({
        publicLinkId: publicLinkId!,
        formId: form.id,
        fileEntitiesIds,
      }),
    );

    updateInitialFormData(form.id, {
      page_field_uuid_to_file_entities_map: result.newInitialPageFieldUuidToFileEntitiesMap,
    });
    updateCurrentFormData(form.id, {
      page_field_uuid_to_file_entities_map: result.newCurrentPageFieldUuidToFileEntitiesMap,
    });
    setFileEntityUuidToFileMap(result.newCurrentFileEntityUuidToFileMap);
    setFailedToCreateFileEntitiesUuids(result.failedToCreateFileEntitiesUuids);

    return result;
  };

  const saveForm = async (form: CustomForm, pageFieldUuidToFileEntitiesMap: CustomFormPageFieldUuidToFileEntitiesMap, fileEntityUuidToFileMap: FileEntityUuidToFileMap): Promise<void> => {
    const {
      allFailed,
      somethingFailed,
    } = await uploadPublicLinkFieldsFiles(form, pageFieldUuidToFileEntitiesMap, fileEntityUuidToFileMap);

    if (allFailed) {
      ToastService.middleScreenErrorToast(t('publicLinkPage.allFilesFailed'));
      return;
    } else if (somethingFailed) {
      ToastService.middleScreenErrorToast(t('publicLinkPage.someFilesFailed'));
      return;
    }

    try {
      const {
        is_task_queued,
        is_form_attached_to_record,
        new_signer_names,
        pdf_file_url
      } = await fillPublicLinkCustomFormReportFormRequest({
        publicLinkId: publicLinkId!,
        form,
      });

      if (pdf_file_url) {
        setPdfPreviewGenerationStatus({
          progress: 100,
          currentStep: t('publicLinkPage.pdfGenerated'),
          pdfFileUrl: pdf_file_url,
          errorDetail: null,
        });
      } else if (is_task_queued) {
        setPdfPreviewGenerationStatus({
          pdfFileUrl: null,
          progress: 0,
          currentStep: t('publicLinkPage.pdfGenerationQueued'),
          errorDetail: null,
        });
      }

      // Analytics: Form Submitted
      trackFormSubmitted({ isFormAttachedToRecord: is_form_attached_to_record, form, company: company! });

      // Analytics: Form Signed
      if (new_signer_names?.length) {
        for (const signerName of new_signer_names) {
          trackFormSigned({ signerName, form, company: company! });
        }
      }

      ToastService.middleScreenToast(t('common.saved'));

      // Wait for 2 seconds so that the user can read the error message
      await wait(1500);

      setIsPublicLinkFilled(true);
    } catch (error: any) {
      console.error('Error saving form:', error);
      ToastService.middleScreenErrorToast(error.message);
    }
  };

  let title: string;

  if (loading) {
    title = t('common.loading');
  } else if (isPublicLinkFilled) {
    title = t('publicLinkPage.submissionSentTitle');
  } else {
    let name: string;
    if (publicLinkFormsData?.length > 1) {
      name = t('publicLinkPage.formsCount', { count: publicLinkFormsData.length });
    } else if (publicLinkFormsData?.length) {
      name = publicLinkFormsData[0].form.name;
    } else {
      name = t('common.unknown');
    }
    title = t('common.pageTitle', { name });
  }

  // Update Page Title
  useEffect(() => {
    onChangePageTitle(title);
  }, [title, onChangePageTitle]);

  if (error) {
    return (
      <MainLayout>
        <ErrorView errorDetails={ error }/>
      </MainLayout>
    );
  }

  if (isPublicLinkFilled) {
    return (
      <MainLayout>
        <GlobalStyle/>
        <SuccessPage message={ t('publicLinkPage.submissionSentMessage') } pdfDetails={ pdfPreviewGenerationStatus }/>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <LocalizationContextProvider maintenanceAgreementName={ company?.maintenance_agreement_name }
                                   maintenanceAgreementNamePlural={ company?.maintenance_agreement_name_plural }>
        <CustomFormReportView
          reportType={ reportType }
          isDraft={ false }
          loading={ loading }
          user={ publicLinkAuthor }
          company={ company }
          customer={ customer }
          fileEntityUuidToFileMap={ fileEntityUuidToFileMap }
          setFileEntityUuidToFileMap={ setFileEntityUuidToFileMap }
          pageUuidToOfflineBackgroundFileUrlMap={ {} }
          formReport={ formReport }
          initialFormsData={ initialPublicLinkFormsData }
          formsData={ publicLinkFormsData }
          onChangeFormsData={ setPublicLinkFormsData }
          failedToCreateFileEntitiesUuids={ failedToCreateFileEntitiesUuids }
          onSaveForm={ saveForm }
        />
      </LocalizationContextProvider>
    </MainLayout>
  );
};
